
import EditableInput from "@/components/ui/editable-input";
import { User, Mail, Phone, Edit, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAutoSave } from "@/hooks/useAutoSave";
import { useAuth } from "@/hooks/useAuth";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useI18n } from '@/hooks/useI18n';

// Profile section component with separate edit state
interface ProfileSectionProps {
  isEditing?: boolean;
  onEditToggle?: () => void;
  onCancelEdit?: () => void;
}

const ProfileSection = ({
  isEditing = false,
  onEditToggle,
  onCancelEdit
}: ProfileSectionProps) => {
  const { userData } = useAuth();
  const { t } = useI18n();
  
  // Local state for form data
  const [formData, setFormData] = useState({
    first_name: userData?.first_name || "",
    last_name: userData?.last_name || "",
    email: userData?.email || "",
    phone: userData?.phone || ""
  });

  // Update local state when userData changes
  useEffect(() => {
    if (userData) {
      setFormData({
        first_name: userData.first_name || "",
        last_name: userData.last_name || "",
        email: userData.email || "",
        phone: userData.phone || ""
      });
    }
  }, [userData]);

  // Create update function that updates both Supabase and global context
  const updateUserProfile = async (updates: Partial<typeof formData>): Promise<{ success: boolean; error?: string }> => {
    try {
      if (!userData?.id) {
        return { 
          success: false, 
          error: t('errors.unauthorized') 
        };
      }

      const { error } = await supabase
        .from('users')
        .update(updates)
        .eq('id', userData.id);

      if (error) {
        console.error('Error updating user profile:', error);
        return { 
          success: false, 
          error: t('profile.updateError') 
        };
      }

      // Force refresh of global context by triggering auth state change
      // This will cause AuthContext to refetch userData from Supabase
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        // Trigger a small update to force context refresh
        await supabase.auth.updateUser({ data: { last_updated: new Date().toISOString() } });
      }

      return { success: true };
      
    } catch (error) {
      console.error('Unexpected error updating user profile:', error);
      return { 
        success: false, 
        error: 'Errore imprevisto durante l\'aggiornamento del profilo' 
      };
    }
  };

  const { saveWithDebounce } = useAutoSave({
    debounceTime: 400,
    showToast: true,
    saveFunction: updateUserProfile
  });

  const handleInputChange = (field: string) => (value: string) => {
    // Update local state immediately
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-save with debounce only when editing and data has actually changed
    if (isEditing && userData && userData[field as keyof typeof userData] !== value) {
      saveWithDebounce({ [field]: value });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-2">
            <User className="h-5 w-5" />
            {t('profile.personalData')}
          </h3>
          <p className="text-sm text-gray-600">{t('profile.basicInfo')}</p>
        </div>
        {onEditToggle && onCancelEdit && (
          <div className="flex gap-2">
            {isEditing ? (
              <Button
                variant="outline"
                onClick={onCancelEdit}
                className="flex items-center gap-2"
              >
                <X className="h-4 w-4" />
                {t('common.cancel')}
              </Button>
            ) : (
              <Button
                onClick={onEditToggle}
                className="flex items-center gap-2"
              >
                <Edit className="h-4 w-4" />
                {t('common.edit')}
              </Button>
            )}
          </div>
        )}
      </div>
      
      {/* Email */}
      <EditableInput
        label={t('auth.email')}
        value={formData.email}
        type="email"
        disabled={true}
        icon={Mail}
        isEditing={isEditing}
        onChange={handleInputChange('email')}
        placeholder={t('register.placeholderEmail')}
      />
      {isEditing && (
        <p className="text-sm text-gray-500">
          {t('profile.emailNotEditable')}
        </p>
      )}

      {/* Name Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <EditableInput
          label={t('profile.firstName')}
          value={formData.first_name}
          icon={User}
          isEditing={isEditing}
          onChange={handleInputChange('first_name')}
          placeholder={t('register.placeholderFirstName')}
        />
        
        <EditableInput
          label={t('profile.lastName')}
          value={formData.last_name}
          icon={User}
          isEditing={isEditing}
          onChange={handleInputChange('last_name')}
          placeholder={t('register.placeholderLastName')}
        />
      </div>

      {/* Phone */}
      <EditableInput
        label={t('profile.phone')}
        value={formData.phone}
        type="tel"
        icon={Phone}
        isEditing={isEditing}
        onChange={handleInputChange('phone')}
        placeholder={t('register.placeholderPhone')}
      />
    </div>
  );
};

export default ProfileSection;
